<template>
  <div class="role-management-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Role Management</h1>
      <p class="text-gray-600">Kelola role dan akses pengguna dalam sistem</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Action Bar -->
      <div class="list-user-role-header bg-gray-50 px-6 py-4">
        <div class="flex items-center gap-3">
          <div class="menu-header-check-all">
            <input 
              type="checkbox" 
              v-model="checkAll" 
              @change="handleCheckAllChange"
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
          </div>
          <span class="text-sm text-gray-600" v-if="items.selected.length > 0">
            {{ items.selected.length }} role dipilih
          </span>
        </div>
        
        <div class="flex items-center gap-2">
          <el-button 
            size="mini" 
            type="default" 
            icon="el-icon-view" 
            v-if="items.selected.length === 1" 
            circle
            title="Lihat Detail"
          ></el-button>
          <el-button 
            size="mini" 
            type="primary" 
            icon="el-icon-plus" 
            v-if="items.selected.length === 0" 
            @click="openDialogVisible" 
            circle
            title="Tambah Role"
          ></el-button>
          <el-button 
            size="mini" 
            type="warning" 
            icon="el-icon-edit" 
            v-if="items.selected.length === 1" 
            @click="openEditDialog" 
            circle
            title="Edit Role"
          ></el-button>
          <el-button 
            size="mini" 
            type="danger" 
            icon="el-icon-delete" 
            v-if="items.selected.length > 0" 
            @click="openDeleteDialog" 
            circle
            title="Hapus Role"
          ></el-button>
        </div>
      </div>

      <!-- Role List -->
      <div class="divide-y divide-gray-200">
        <div 
          v-if="roleMapping.length === 0"
          class="px-6 py-12 text-center"
        >
          <div class="flex flex-col items-center">
            <svg class="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
            </svg>
            <p class="text-gray-500 text-lg font-medium">Tidak ada role tersedia</p>
            <p class="text-gray-400 text-sm mt-1">Klik tombol + untuk menambah role baru</p>
          </div>
        </div>

        <div 
          v-for="(item, index) in roleMapping" 
          :key="index"
          class="list-role-management-content px-6 py-4 hover:bg-gray-50 transition-colors duration-150"
        >
          <div class="flex items-center flex-1">
            <div class="role-zero mr-4">
              <input 
                type="checkbox" 
                :value="item" 
                :label="index" 
                v-model="items.selected" 
                @change="handleCheckChange"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </div>
            <div class="role-first flex-1">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-blue-100 rounded-lg">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="role-name font-semibold text-gray-800">{{ item.roleName }}</p>
                  <p class="text-sm text-gray-500 mt-0.5">{{ item.roleCode }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination Info (Optional) -->
      <div v-if="roleMapping.length > 0" class="px-6 py-4 bg-gray-50 border-t">
        <p class="text-sm text-gray-600">
          Total: <span class="font-semibold">{{ roleMapping.length }}</span> role
        </p>
      </div>
    </div>

    <!-- Add Role Dialog -->
    <el-dialog 
      title="Tambah Role" 
      :visible.sync="dialogVisible" 
      width="30%"
      :close-on-click-modal="false"
    >
      <div class="space-y-4">
        <div class="role-form">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Role Code
          </label>
          <el-input 
            v-model="form.roleCode" 
            placeholder="Masukkan kode role (contoh: ROLE_ADMIN)"
            class="w-full"
          ></el-input>
        </div>
        <div class="role-form">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Role Name
          </label>
          <el-input 
            v-model="form.roleName" 
            placeholder="Masukkan nama role (contoh: Administrator)"
            class="w-full"
          ></el-input>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="dialogVisible = false">Batal</el-button>
        <el-button 
          size="mini" 
          type="primary" 
          @click="saveDialogVisible" 
          :loading="loading"
          :disabled="!form.roleCode || !form.roleName"
        >
          Simpan
        </el-button>
      </span>
    </el-dialog>

    <!-- Edit Role Dialog -->
    <el-dialog 
      title="Edit Role" 
      :visible.sync="editDialogVisible" 
      width="30%"
      :close-on-click-modal="false"
    >
      <div class="space-y-4">
        <div class="role-form">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Role Code
          </label>
          <el-input 
            v-model="editForm.roleCode" 
            placeholder="Masukkan kode role" 
            disabled
            class="w-full"
          ></el-input>
          <p class="text-xs text-gray-500 mt-1">Role code tidak dapat diubah</p>
        </div>
        <div class="role-form">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Role Name
          </label>
          <el-input 
            v-model="editForm.roleName" 
            placeholder="Masukkan nama role"
            class="w-full"
          ></el-input>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="editDialogVisible = false">Batal</el-button>
        <el-button 
          size="mini" 
          type="primary" 
          @click="saveEditDialog" 
          :loading="loading"
          :disabled="!editForm.roleName"
        >
          Update
        </el-button>
      </span>
    </el-dialog>

    <!-- Delete Confirmation Dialog -->
    <el-dialog 
      title="Konfirmasi Hapus" 
      :visible.sync="deleteDialogVisible" 
      width="30%"
      :close-on-click-modal="false"
    >
      <div>
        <el-alert
          title="Peringatan!"
          type="warning"
          description="Apakah Anda yakin ingin menghapus role yang dipilih? Tindakan ini tidak dapat dibatalkan."
          show-icon
          :closable="false"
        ></el-alert>
        
        <div class="mt-5">
          <p class="font-semibold text-gray-800 mb-3">Role yang akan dihapus:</p>
          <div class="bg-red-50 border border-red-200 rounded-lg p-3 max-h-60 overflow-y-auto">
            <ul class="space-y-2">
              <li 
                v-for="(item, index) in items.selected" 
                :key="index"
                class="flex items-center gap-2 text-gray-700"
              >
                <i class="el-icon-close text-red-500"></i>
                <span class="font-medium">{{ item.roleName }}</span>
                <span class="text-sm text-gray-500">({{ item.roleCode }})</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="deleteDialogVisible = false">Batal</el-button>
        <el-button 
          size="mini" 
          type="danger" 
          @click="confirmDelete" 
          :loading="loading"
        >
          <i class="el-icon-delete mr-1"></i>
          Hapus
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
export default {
  name: "RoleManagement",
  data() {
    return {
      loading : false,
      checkAll: false,
      isIndeterminate: false,
      items: { selected: [] },
      roleMapping: [],
      dialogVisible: false,
      editDialogVisible: false,
      deleteDialogVisible: false,
      form: { roleCode: null, roleName: null },
      editForm: { roleCode: null, roleName: null, id: null }
    }
  },
  created() {
    this.getFetchRole();
  },
  methods: {
    getAuthToken() {
      return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
    },
    handleCheckAllChange(event) {
      // Ketika check all diklik, pilih atau hapus semua item (simpan seluruh data item)
      this.items.selected = event.target.checked ? [...this.roleMapping] : [];
      this.isIndeterminate = false;
    },
    handleCheckChange() {
      // Ketika individual checkbox berubah, update status check all
      const checkedCount = this.items.selected.length;
      this.checkAll = checkedCount === this.roleMapping.length;
      this.isIndeterminate = checkedCount > 0 && checkedCount < this.roleMapping.length;
    },
    openDialogVisible() {
      this.dialogVisible = true;
      this.loading = false;
      this.form.roleCode = null;
      this.form.roleName = null;
    },
    openEditDialog() {
      if (this.items.selected.length === 1) {
        const selectedRole = this.items.selected[0];
        this.editForm.id = selectedRole.id;
        this.editForm.roleCode = selectedRole.roleCode;
        this.editForm.roleName = selectedRole.roleName;
        this.editDialogVisible = true;
      }
    },
    saveEditDialog() {
      this.loading = true;
      this.$store.dispatch('roles/updateRole', { 
        id: this.editForm.id,
        roleCode: this.editForm.roleCode, 
        roleName: this.editForm.roleName,
        token: this.getAuthToken()
      })
      .then(() => {
        this.loading = false;
        this.editDialogVisible = false;
        this.items.selected = [];
        this.checkAll = false;
        // Refresh data setelah update
        this.roleMapping = [];
        this.getFetchRole();
        this.$message.success('Role berhasil diupdate');
      })
      .catch((error) => {
        this.loading = false;
        this.$message.error('Gagal mengupdate role: ' + (error.message || 'Unknown error'));
      }); 
    },
    openDeleteDialog() {
      if (this.items.selected.length > 0) {
        this.deleteDialogVisible = true;
      }
    },
    confirmDelete() {
      this.loading = true;
      const deletePromises = this.items.selected.map(role => 
        this.$store.dispatch('roles/deleteRole', { 
          id: role.id,
          token: this.getAuthToken()
        })
      );
      
      Promise.all(deletePromises)
        .then(() => {
          this.loading = false;
          this.deleteDialogVisible = false;
          this.items.selected = [];
          this.checkAll = false;
          // Refresh data setelah delete
          this.roleMapping = [];
          this.getFetchRole();
          this.$message.success('Role berhasil dihapus');
        })
        .catch((error) => {
          this.loading = false;
          this.$message.error('Gagal menghapus role: ' + (error.message || 'Unknown error'));
        });
    },
    saveDialogVisible() {
      this.loading = true;
      this.$store.dispatch('roles/createRoleEnable', { roleCode: this.form.roleCode, roleName: this.form.roleName })
      .then(() => {
        this.loading = false;
        this.dialogVisible = false;
        this.form.roleCode = null;
        this.form.roleName = null;
        // Refresh data setelah menyimpan
        this.roleMapping = []; // Clear existing data
        this.getFetchRole(); // Fetch updated data
        this.$message.success('Role berhasil ditambahkan');
      })
      .catch((error) => {
        this.loading = false;
        this.$message.error('Gagal menambahkan role: ' + (error.message || 'Unknown error'));
      }); 
    },
    async getFetchRole() {
      this.loading = true;
      try {
        // Buat array promises untuk semua dispatch dengan tracking roleName
        const promises = [];
        // Simpan mapping index -> roleName
        // const roleMapping = [];

        // Push semua dispatch ke array promises dan simpan true
        promises.push(
          this.$store.dispatch('roles/fetchRoleTrue', { 
            token: this.getAuthToken()
          })
        );

        // Push semua dispatch ke array promises dan simpan false
        promises.push(
          this.$store.dispatch('roles/fetchRoleFalse', { 
            token: this.getAuthToken()
          })
        );
        
        // Jalankan semua promises secara parallel dan tunggu semua selesai
        const results = await Promise.all(promises);

        // Process results: ambil data saja, skip jika data kosong atau data = []
        const processedData = results.map(result => result && result.data && result.data.length > 0 ? result.data : null).filter(data => data !== null);  
        
        processedData.forEach(dataSet => {
          dataSet.forEach(role => {
            this.roleMapping.push(role);
          });
        }); 
        
        return this.roleMapping;
      } catch (error) {
        console.error('Error fetching role data:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>
<style scoped>
.role-management-container {
  max-width: 1200px;
  margin: 0 auto;
}

.list-user-role-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.list-role-management-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.role-first {
  flex-grow: 1;
}

.role-form {
  margin-bottom: 1rem;
}

/* Custom checkbox styling */
input[type="checkbox"] {
  cursor: pointer;
}

input[type="checkbox"]:hover {
  opacity: 0.8;
}

/* Dialog improvements */
:deep(.el-dialog) {
  border-radius: 12px;
  overflow: hidden;
}

:deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  margin: 0;
}

:deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
  font-size: 18px;
}

:deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
  font-size: 20px;
}

:deep(.el-dialog__body) {
  padding: 24px;
}

:deep(.el-dialog__footer) {
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

/* Input styling */
:deep(.el-input__inner) {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  transition: all 0.2s;
}

:deep(.el-input__inner:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

:deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

/* Alert styling */
:deep(.el-alert) {
  border-radius: 8px;
}

/* Button improvements */
:deep(.el-button) {
  transition: all 0.2s;
}

:deep(.el-button--mini.is-circle) {
  width: 32px;
  height: 32px;
  padding: 0;
}

/* Scrollbar styling */
.max-h-60::-webkit-scrollbar {
  width: 6px;
}

.max-h-60::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.max-h-60::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.max-h-60::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .role-management-container {
    padding: 1rem;
  }

  :deep(.el-dialog) {
    width: 90% !important;
  }

  .list-user-role-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}

/* Empty state animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.flex.flex-col.items-center {
  animation: fadeIn 0.3s ease-in-out;
}
</style>